import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, getAuthToken } from "@/lib/api";

/**
 * useChunkedUpload — drives the backend's chunked upload-session lifecycle
 * (POST /session → PUT /chunk × N → POST /complete) for one File at a time.
 *
 * Single-shot uploads break for >100 MB files behind Cloudflare's free tier
 * and have no resumability. The backend's chunked endpoints already exist
 * (routers/uploads.py); this hook is the client.
 *
 * Server contract:
 * - Default chunk size is 16 MB (`upload_chunk_size_bytes` setting).
 * - PUT chunks must be strict-ordered. Re-PUTing `index < next_index` is a
 *   no-op (idempotent retry). PUTing `index > next_index` is 409.
 * - The first chunk's hash (SHA-256 of first 64 KB) lets the server bind
 *   the session to this file. Reusing an existing active session with the
 *   same {match, slot, size, ext, hash} resumes from its `next_index`.
 *
 * Cross-reload resume:
 * - On session create, the hook persists {session_id, file_name, file_size,
 *   started_at} to localStorage keyed by {match_id, slot}. On modal reopen
 *   with a re-picked file the consumer calls `discoverActiveSession(...)`
 *   which validates the stored entry against the picked file AND against
 *   the server (session must still be active, first-chunk hash must match).
 *   If everything aligns the consumer renders a resume prompt and passes
 *   `resumeFrom` to `start()` which skips POST /session and starts the
 *   chunk loop at the existing `next_index`.
 */

export type ChunkedUploadPhase =
  | "idle"
  | "hashing"
  | "creating-session"
  | "uploading"
  | "completing"
  | "done"
  | "error"
  | "cancelled";

export interface ChunkedUploadState {
  phase: ChunkedUploadPhase;
  bytesUploaded: number;
  totalBytes: number;
  currentChunkIndex: number;
  totalChunks: number;
  error: string | null;
}

interface SessionResponse {
  session_id: string;
  chunk_size: number;
  total_chunks: number;
  next_index: number;
}

/** Stored in localStorage so a tab-close → reopen can offer to resume. */
interface StoredSession {
  session_id: string;
  file_name: string;
  file_size: number;
  started_at: number;
}

/** Returned by `discoverActiveSession` when a stored session matches the
 * currently-picked file AND is still active on the server. The modal uses
 * this to render the "resume previous upload?" prompt. */
export interface ResumableSession {
  session_id: string;
  next_index: number;
  total_chunks: number;
  chunk_size: number;
  size_bytes: number;
  /** Bytes already on the server. Computed from next_index × chunk_size,
   * clamped to file size. The modal uses this for the resume prompt's
   * "uploaded X of Y MB" line. */
  bytes_uploaded: number;
}

const MAX_CHUNK_RETRIES = 3;
const RETRY_BACKOFF_MS = [500, 2000, 5000];

const STORAGE_KEY_PREFIX = "tktic.upload-session.";

function storageKey(matchId: string, slot: string): string {
  return `${STORAGE_KEY_PREFIX}${matchId}.${slot}`;
}

function getStoredSession(matchId: string, slot: string): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(storageKey(matchId, slot));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.session_id === "string"
      && typeof parsed.file_name === "string"
      && typeof parsed.file_size === "number"
      && typeof parsed.started_at === "number"
    ) {
      return parsed as StoredSession;
    }
    return null;
  } catch {
    return null;
  }
}

function setStoredSession(matchId: string, slot: string, entry: StoredSession): void {
  try {
    window.localStorage.setItem(storageKey(matchId, slot), JSON.stringify(entry));
  } catch {
    // localStorage quota / private-browsing fallback: silent. Worst case is
    // no cross-reload resume, which is the pre-PR-90 behavior anyway.
  }
}

function clearStoredSession(matchId: string, slot: string): void {
  try {
    window.localStorage.removeItem(storageKey(matchId, slot));
  } catch {
    /* same fallback rationale as setStoredSession */
  }
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new Error(
      "crypto.subtle unavailable — page must be served over HTTPS or localhost",
    );
  }
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Race a promise against a timeout. Used to break hangs in the pre-upload
 * preparation steps (file.slice read, SHA-256 digest) without leaving the
 * modal stuck forever. If the timeout wins the promise rejects with a
 * descriptive Error; the caller can then choose to continue without the
 * optional value (e.g. skip the first_chunk_hash). */
function withTimeout<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(what)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

interface XhrResult {
  status: number;
  responseText: string;
}

function sendChunk(
  url: string,
  blob: Blob,
  onProgress: (loaded: number) => void,
  abortSignal: { aborted: boolean; xhrRef: { current: XMLHttpRequest | null } },
): Promise<XhrResult> {
  return new Promise((resolve, reject) => {
    if (abortSignal.aborted) {
      reject(new ApiError(0, "Upload cancelled"));
      return;
    }
    const xhr = new XMLHttpRequest();
    abortSignal.xhrRef.current = xhr;
    xhr.open("PUT", url);
    const token = getAuthToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.onload = () => {
      abortSignal.xhrRef.current = null;
      resolve({ status: xhr.status, responseText: xhr.responseText });
    };
    xhr.onerror = () => {
      abortSignal.xhrRef.current = null;
      reject(new ApiError(0, "Network error"));
    };
    xhr.onabort = () => {
      abortSignal.xhrRef.current = null;
      reject(new ApiError(0, "Upload cancelled"));
    };
    xhr.send(blob);
  });
}

function describeHttpError(status: number, responseText: string): string {
  let detail: string | null = null;
  try {
    const body = JSON.parse(responseText) as { detail?: unknown };
    if (typeof body.detail === "string") detail = body.detail;
  } catch {
    /* fall through */
  }
  const label = `HTTP ${status}`;
  return detail ? `${label}: ${detail}` : label;
}

async function jsonRequest(
  method: string,
  url: string,
  body: unknown,
): Promise<unknown> {
  const token = getAuthToken();
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, describeHttpError(res.status, text));
  }
  return text ? JSON.parse(text) : null;
}

/** Check whether a stored localStorage entry matches the picked file AND
 * a still-active session on the server. Exposed so the modal can render
 * a "resume previous upload?" prompt when the user re-opens the modal
 * with the same file after a tab close / reload.
 *
 * Returns null if any check fails — including filename/size mismatch,
 * server session expired / cancelled, or the server-side first-chunk
 * hash doesn't match the picked file (defense against same-name-different-
 * content collisions).
 *
 * Stale localStorage entries are cleared as a side effect when detected,
 * so callers can re-call this and get a consistent fresh state.
 */
async function discoverActiveSession(
  file: File,
  matchId: string,
  slot: "full" | "first_half" | "second_half",
): Promise<ResumableSession | null> {
  const stored = getStoredSession(matchId, slot);
  if (!stored) return null;

  // Cheap pre-check: filename + size must match. Picking a different file
  // clears the entry (the resume can't apply to a file we don't have).
  if (stored.file_name !== file.name || stored.file_size !== file.size) {
    clearStoredSession(matchId, slot);
    return null;
  }

  // Server check: session must still exist and be active.
  interface ServerSessionView {
    session_id?: string;
    status?: string;
    next_index?: number;
    total_chunks?: number;
    chunk_size?: number;
    size_bytes?: number;
    first_chunk_hash?: string | null;
  }
  let sessionView: ServerSessionView | null = null;
  try {
    sessionView = (await jsonRequest(
      "GET",
      `/api/uploads/sessions/${encodeURIComponent(stored.session_id)}`,
      undefined,
    )) as ServerSessionView;
  } catch (err) {
    // 404 / 409 / 403 → session gone. Clear and bail.
    if (err instanceof ApiError && err.status > 0) {
      clearStoredSession(matchId, slot);
    }
    return null;
  }
  if (!sessionView || sessionView.status !== "active") {
    clearStoredSession(matchId, slot);
    return null;
  }

  // Hash verification: same filename + same size + different content is rare
  // but possible (re-edited video saved with same name, identical byte length).
  // Compare against the hash the server recorded when the session was created.
  if (sessionView.first_chunk_hash) {
    try {
      const firstSlice = await withTimeout(
        file.slice(0, 64 * 1024).arrayBuffer(),
        8000,
        "file.slice(0, 64KB).arrayBuffer() timed out",
      );
      const localHash = await withTimeout(
        sha256Hex(firstSlice),
        4000,
        "SHA-256 of first 64KB timed out",
      );
      if (localHash !== sessionView.first_chunk_hash) {
        clearStoredSession(matchId, slot);
        return null;
      }
    } catch {
      // Hash step failed (crypto.subtle unavailable, etc.). Be conservative:
      // if we can't verify, refuse to offer resume. Worst case the user gets
      // a fresh upload, which is correct behavior — no data lost on the
      // server, the stale-session sweeper cleans up eventually.
      return null;
    }
  }

  const chunkSize = Number(sessionView.chunk_size ?? 0);
  const nextIndex = Number(sessionView.next_index ?? 0);
  const totalChunks = Number(sessionView.total_chunks ?? 0);
  const sizeBytes = Number(sessionView.size_bytes ?? file.size);
  const bytesUploaded = Math.min(nextIndex * chunkSize, sizeBytes);

  return {
    session_id: stored.session_id,
    next_index: nextIndex,
    total_chunks: totalChunks,
    chunk_size: chunkSize,
    size_bytes: sizeBytes,
    bytes_uploaded: bytesUploaded,
  };
}

export function useChunkedUpload() {
  const [state, setState] = useState<ChunkedUploadState>({
    phase: "idle",
    bytesUploaded: 0,
    totalBytes: 0,
    currentChunkIndex: 0,
    totalChunks: 0,
    error: null,
  });

  // Cancellation is shared between the running pipeline and the user-facing
  // cancel() call. The xhrRef lets cancel() abort the in-flight chunk PUT.
  // matchId/slot are stored so cancel() can clear the localStorage entry.
  const cancelRef = useRef<{
    aborted: boolean;
    xhrRef: { current: XMLHttpRequest | null };
    sessionId: string | null;
    matchId: string | null;
    slot: string | null;
  }>({
    aborted: false,
    xhrRef: { current: null },
    sessionId: null,
    matchId: null,
    slot: null,
  });

  const mountedRef = useRef(true);
  // Mirror `state` into a ref so `cancel()` can read the current phase
  // without taking `state` as a dep (which would re-create cancel on every
  // render and force every consumer's effect deps to re-run mid-upload).
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      // Unmount-time cleanup: stop any in-flight chunk PUT, but DO NOT
      // fire the DELETE /session here. If the modal unmounts during
      // `phase === "completing"` the server-side complete request is
      // already in flight; we don't want to race a DELETE against it.
      // Stale-session sweeper handles abandoned sessions either way.
      // Crucially we DON'T clear localStorage on unmount either — that's
      // the whole point of cross-reload resume: the entry must survive a
      // tab close / navigation so a later mount can pick it up.
      cancelRef.current.aborted = true;
      cancelRef.current.xhrRef.current?.abort();
    };
  }, []);

  const safeSet = useCallback(
    (
      updater: (prev: ChunkedUploadState) => ChunkedUploadState,
    ) => {
      if (mountedRef.current) setState(updater);
    },
    [],
  );

  const cancel = useCallback(() => {
    // No-op once we've handed off to the server's `complete` step. By the
    // time `phase === "completing"` the chunked PUTs have all succeeded
    // and `POST /complete` is in flight; that endpoint flips the session
    // to `completed` and spawns transcode. If we fired `DELETE /session`
    // here it could race in AFTER the server marked the session completed,
    // and the cancel handler unconditionally unlinks raw_path — which
    // would corrupt a successful multi-GB upload mid-transcode. The
    // backend also enforces this (returns 409 for non-active sessions)
    // but we belt-and-brace it here so the network call doesn't even
    // leave the browser.
    const phase = stateRef.current.phase;
    if (phase === "completing" || phase === "done" || phase === "cancelled") {
      return;
    }
    cancelRef.current.aborted = true;
    cancelRef.current.xhrRef.current?.abort();
    const sid = cancelRef.current.sessionId;
    if (sid) {
      // Fire-and-forget: backend's stale-session sweeper will clean up
      // anyway if this DELETE fails. We don't want cancel() to block.
      jsonRequest("DELETE", `/api/uploads/sessions/${sid}`, undefined).catch(
        () => undefined,
      );
    }
    // User-initiated cancel discards the session intentionally — wipe the
    // localStorage entry so the next modal open doesn't offer to resume a
    // session that we just told the server to delete.
    if (cancelRef.current.matchId && cancelRef.current.slot) {
      clearStoredSession(cancelRef.current.matchId, cancelRef.current.slot);
    }
    safeSet((prev) => ({ ...prev, phase: "cancelled" }));
  }, [safeSet]);

  const start = useCallback(
    async (
      file: File,
      opts: {
        matchId: string;
        slot: "full" | "first_half" | "second_half";
        resumeFrom?: ResumableSession;
      },
    ): Promise<void> => {
      console.log(
        "[upload] start() called",
        {
          matchId: opts.matchId,
          slot: opts.slot,
          fileName: file.name,
          fileSize: file.size,
          resumeFromSession: opts.resumeFrom?.session_id ?? null,
        },
      );
      cancelRef.current = {
        aborted: false,
        xhrRef: { current: null },
        sessionId: opts.resumeFrom?.session_id ?? null,
        matchId: opts.matchId,
        slot: opts.slot,
      };
      safeSet(() => ({
        phase: opts.resumeFrom ? "uploading" : "hashing",
        bytesUploaded: opts.resumeFrom?.bytes_uploaded ?? 0,
        totalBytes: file.size,
        currentChunkIndex: opts.resumeFrom?.next_index ?? 0,
        totalChunks: opts.resumeFrom?.total_chunks ?? 0,
        error: null,
      }));

      try {
        let session: SessionResponse;

        if (opts.resumeFrom) {
          // Skip POST /session entirely — the caller already verified the
          // session is active via discoverActiveSession. Trust the values
          // they passed in. The first chunk's hash isn't re-uploaded; the
          // server bound it on initial session creation.
          session = {
            session_id: opts.resumeFrom.session_id,
            chunk_size: opts.resumeFrom.chunk_size,
            total_chunks: opts.resumeFrom.total_chunks,
            next_index: opts.resumeFrom.next_index,
          };
          console.log(
            "[upload] resuming from chunk",
            session.next_index,
            "of",
            session.total_chunks,
          );
        } else {
          // Hash the first 64 KB of the file so the server can detect a
          // stale-session resume against a different file with matching
          // (size, ext) fingerprint. The hash is OPTIONAL on the server
          // (Pydantic field is Optional[str]) — if the browser environment
          // somehow can't produce one (crypto.subtle unavailable on a
          // non-secure context, or a hung file.slice on a misbehaving
          // Chrome build), skip it and continue. We log the failure so the
          // operator can see it in the console but DON'T block the upload.
          let firstChunkHash: string | undefined;
          try {
            console.log("[upload] hashing first 64KB of", file.name, file.size);
            const firstSlice = await withTimeout(
              file.slice(0, 64 * 1024).arrayBuffer(),
              8000,
              "file.slice(0, 64KB).arrayBuffer() timed out",
            );
            firstChunkHash = await withTimeout(
              sha256Hex(firstSlice),
              4000,
              "SHA-256 of first 64KB timed out",
            );
            console.log("[upload] hash =", firstChunkHash);
          } catch (hashErr) {
            console.warn(
              "[upload] hash step failed, continuing without it:",
              hashErr,
            );
            // continue with firstChunkHash undefined
          }
          if (cancelRef.current.aborted) throw new ApiError(0, "Upload cancelled");

          safeSet((prev) => ({ ...prev, phase: "creating-session" }));
          const sessionUrl = `/api/matches/${encodeURIComponent(opts.matchId)}/upload-video/session?slot=${opts.slot}`;
          console.log("[upload] POST", sessionUrl);
          session = (await jsonRequest("POST", sessionUrl, {
            filename: file.name,
            size_bytes: file.size,
            // Only include the hash if we successfully computed one. The
            // backend treats missing first_chunk_hash as "no defense-in-depth
            // resume binding" which is fine for a fresh upload.
            ...(firstChunkHash ? { first_chunk_hash: firstChunkHash } : {}),
          })) as SessionResponse;
          console.log("[upload] session created:", session.session_id, "chunks:", session.total_chunks);
          cancelRef.current.sessionId = session.session_id;

          // Persist for cross-reload resume. Written BEFORE the first chunk
          // PUT so even a hard tab-close after the session create can be
          // resumed from chunk 0 (which is a server-side no-op anyway).
          setStoredSession(opts.matchId, opts.slot, {
            session_id: session.session_id,
            file_name: file.name,
            file_size: file.size,
            started_at: Date.now(),
          });
        }

        // Resume: start from server's next_index. Backend has already
        // accepted chunks [0, next_index), so we count those as uploaded.
        const startIndex = session.next_index;
        const skippedBytes = Math.min(
          startIndex * session.chunk_size,
          file.size,
        );
        safeSet((prev) => ({
          ...prev,
          phase: "uploading",
          totalChunks: session.total_chunks,
          currentChunkIndex: startIndex,
          bytesUploaded: skippedBytes,
        }));

        for (let i = startIndex; i < session.total_chunks; i++) {
          if (cancelRef.current.aborted) throw new ApiError(0, "Upload cancelled");
          const chunkStart = i * session.chunk_size;
          const chunkEnd = Math.min(chunkStart + session.chunk_size, file.size);
          const chunkBlob = file.slice(chunkStart, chunkEnd);
          console.log(
            "[upload] chunk", i + 1, "/", session.total_chunks,
            `(${chunkStart}-${chunkEnd} of ${file.size})`,
          );

          safeSet((prev) => ({ ...prev, currentChunkIndex: i }));

          let lastError: ApiError | null = null;
          let ok = false;
          for (let attempt = 0; attempt <= MAX_CHUNK_RETRIES; attempt++) {
            if (cancelRef.current.aborted) {
              throw new ApiError(0, "Upload cancelled");
            }
            try {
              const chunkUrl = `/api/uploads/sessions/${session.session_id}/chunk?index=${i}`;
              const result = await sendChunk(
                chunkUrl,
                chunkBlob,
                (loaded) => {
                  safeSet((prev) => ({
                    ...prev,
                    bytesUploaded: skippedBytes
                      + (i - startIndex) * session.chunk_size
                      + loaded,
                  }));
                },
                cancelRef.current,
              );
              if (result.status >= 200 && result.status < 300) {
                ok = true;
                break;
              }
              // 4xx errors are not retryable except 408 / 429.
              const retryable =
                result.status === 408
                || result.status === 429
                || result.status >= 500;
              lastError = new ApiError(
                result.status,
                describeHttpError(result.status, result.responseText),
              );
              if (!retryable) break;
            } catch (err) {
              if (err instanceof ApiError && err.status === 0
                  && err.detail === "Upload cancelled") {
                throw err;
              }
              lastError = err instanceof ApiError
                ? err
                : new ApiError(0, "Network error");
            }
            const backoff = RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)];
            await new Promise((r) => setTimeout(r, backoff));
          }
          if (!ok) {
            throw lastError ?? new ApiError(0, "Chunk upload failed");
          }
          // Lock in the chunk's full size for the running total so the next
          // chunk's onprogress doesn't double-count.
          safeSet((prev) => ({
            ...prev,
            bytesUploaded: skippedBytes
              + (i - startIndex + 1) * session.chunk_size > file.size
                ? file.size
                : skippedBytes + (i - startIndex + 1) * session.chunk_size,
          }));
        }

        if (cancelRef.current.aborted) throw new ApiError(0, "Upload cancelled");
        safeSet((prev) => ({ ...prev, phase: "completing" }));
        await jsonRequest(
          "POST",
          `/api/uploads/sessions/${session.session_id}/complete`,
          undefined,
        );
        // Success — clear the localStorage entry. Any future modal open
        // for this {match, slot} starts fresh.
        clearStoredSession(opts.matchId, opts.slot);
        safeSet((prev) => ({
          ...prev,
          phase: "done",
          bytesUploaded: file.size,
        }));
      } catch (err) {
        if (cancelRef.current.aborted) {
          safeSet((prev) => ({ ...prev, phase: "cancelled" }));
          return;
        }
        const message = err instanceof ApiError
          ? typeof err.detail === "string" ? err.detail : "Upload failed."
          : "Upload failed.";
        safeSet((prev) => ({ ...prev, phase: "error", error: message }));
        throw err;
      }
    },
    [safeSet],
  );

  /** Wipe the localStorage entry for a given {match, slot}. Used by the
   * modal's "Start over" button: the user explicitly chose not to resume,
   * so the entry should not survive a modal close. Server-side cleanup
   * (mark `cancelled`, unlink raw) is handled by the next POST /session
   * via `cancel_conflicting_sessions` and ultimately the stale-session
   * sweeper — this just removes the local trigger for the resume offer. */
  const clearStoredSessionFor = useCallback(
    (matchId: string, slot: "full" | "first_half" | "second_half") => {
      clearStoredSession(matchId, slot);
    },
    [],
  );

  return { state, start, cancel, discoverActiveSession, clearStoredSessionFor };
}
